import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { getAuthStatus } from "../api";
import Auth from "./Auth";

const LillaLandingPage: React.FC = () => {
  const { user, loading, isAuthRequired, isAuthAvailable } = useAuth();
  const [isCreating, setIsCreating] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authStatus, setAuthStatus] = useState<any>(null);

  // Получаем статус авторизации при загрузке
  useEffect(() => {
    const fetchAuthStatus = async () => {
      try {
        const status = await getAuthStatus();
        setAuthStatus(status);
      } catch (error) {
        console.error('Error fetching auth status:', error);
      }
    };

    fetchAuthStatus();
  }, []);

  // Проверяем, нужна ли авторизация
  const needsAuth = authStatus?.auth_enabled && authStatus?.require_login && !user;

  async function handleCreateGameClick() {
    // Если пользователь не авторизован - показываем форму авторизации
    if (needsAuth) {
      setShowAuth(true);
      return;
    }
    
    // Если пользователь авторизован - перенаправляем на создание игры
    const encodedPlayerName = encodeURIComponent(user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || '');
    window.location.href = `/magic?playerName=${encodedPlayerName}&redirectTo=${encodeURIComponent('/')}`;
  }

  // Показываем загрузку пока проверяем авторизацию
  if (loading || !authStatus) {
    return (
      <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Загрузка...</span>
          </div>
          <p className="text-muted">Загружаем приложение...</p>
        </div>
      </div>
    );
  }

  // Показываем форму авторизации
  if (showAuth && isAuthAvailable) {
    return (
      <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center">
        <div className="row justify-content-center w-100">
          <div className="col-md-6 col-lg-4">
            <Auth onClose={() => setShowAuth(false)} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="hero-container">
        <div className="hero-section">
          <div className="container">
            {/* Первый экран (Hero) */}
            <div className="hero-header text-center mb-5">
              <h1 className="hero-title mb-3">Игра Лила онлайн — теперь с AI-наставником 24/7</h1>
              <h2 className="hero-subtitle mb-4">Трансформационная игра нового поколения. Начни прямо сейчас: без ожиданий, без групп, в удобном онлайн-формате.</h2>

              {/* CTA кнопка */}
              <div className="hero-cta-buttons d-flex flex-column align-items-center gap-2">
                <button
                  className={`btn btn-primary btn-lg hero-cta-button ${isCreating ? "magic-animation" : ""}`}
                  onClick={handleCreateGameClick}
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Создаём вашу игру…
                    </>
                  ) : (
                    <>🚀 Войти и начать игру</>
                  )}
                </button>
                <small className="text-muted" style={{fontSize: '12px'}}>
                  Авторизация — чтобы сохранить прогресс и выслать отчёт
                </small>
              </div>
            </div>

            {/* Почему именно Лила? */}
            <section className="mb-5" id="why-lila">
              <div className="row">
                <div className="col-lg-10 mx-auto">
                  <div className="value-card">
                    <h3 className="value-title mb-4">🎯 Почему именно Лила?</h3>
                    <h4 className="mb-4">Что делает «Лилу» особенной?</h4>
                    <div className="row g-4">
                      <div className="col-lg-6 col-md-6">
                        <div className="feature-item">
                          <div className="feature-icon">🎯</div>
                          <div className="feature-content">
                            <h5>72 клетки — зеркало жизненных сценариев</h5>
                            <p>Деньги, отношения, самореализация</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-6">
                        <div className="feature-item">
                          <div className="feature-icon">🐍</div>
                          <div className="feature-content">
                            <h5>Змеи и стрелы — метафоры падений и прорывов</h5>
                            <p>Понимание жизненных циклов</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-6">
                        <div className="feature-item">
                          <div className="feature-icon">🤖</div>
                          <div className="feature-content">
                            <h5>AI-наставник — помогает понять суть и найти выход</h5>
                            <p>Персональное сопровождение</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-6">
                        <div className="feature-item">
                          <div className="feature-icon">🎲</div>
                          <div className="feature-content">
                            <h5>Каждая партия уникальна</h5>
                            <p>Поле отражает именно твой запрос</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-center mt-4">
                      <button
                        className={`btn btn-primary btn-lg hero-cta-button ${isCreating ? "magic-animation" : ""}`}
                        onClick={handleCreateGameClick}
                        disabled={isCreating}
                      >
                        {isCreating ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Создаём вашу игру…
                          </>
                        ) : (
                          <>✨ Попробовать первую игру прямо сейчас</>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Наше отличие */}
            <section className="mb-5" id="our-difference">
              <div className="row">
                <div className="col-lg-10 mx-auto">
                  <div className="value-card">
                    <h3 className="value-title mb-4">🌐 Наше отличие</h3>
                    <h4 className="mb-4">Классическая Лила vs Лила с AI-наставником</h4>
                    
                    {/* Desktop table */}
                    <div className="d-none d-lg-block">
                      <div className="table-responsive">
                        <table className="table table-borderless comparison-table">
                          <thead>
                            <tr>
                              <th className="text-center">Критерий</th>
                              <th className="text-center">Лила онлайн с ведущим</th>
                              <th className="text-center">Лила с AI-наставником</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td><strong>Начало игры</strong></td>
                              <td>Нужно записаться, ждать группу или время мастера</td>
                              <td>Мгновенный старт 24/7, в любое время</td>
                            </tr>
                            <tr>
                              <td><strong>Ведущий</strong></td>
                              <td>Человек-проводник, качество зависит от его опыта</td>
                              <td>Персональный AI-наставник, внимателен к твоему запросу</td>
                            </tr>
                            <tr>
                              <td><strong>Формат</strong></td>
                              <td>Онлайн по видеосвязи или оффлайн, 3–10 часов</td>
                              <td>Онлайн в чате, гибко по времени (от 20 мин до нескольких часов)</td>
                            </tr>
                            <tr>
                              <td><strong>Стоимость</strong></td>
                              <td>От $40 за групповую и от $80 за индивидуальную сессию</td>
                              <td>Доступный формат — AI работает без ограничений</td>
                            </tr>
                            <tr>
                              <td><strong>Комфорт</strong></td>
                              <td>Часто в группе, нужно делиться личным</td>
                              <td>Полная анонимность и индивидуальная работа</td>
                            </tr>
                            <tr>
                              <td><strong>Результат</strong></td>
                              <td>Зависит от качества ведущего и группы</td>
                              <td>Персонализированный отчёт и ясный план после игры</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Mobile cards */}
                    <div className="d-lg-none">
                      <div className="row g-3">
                        <div className="col-12">
                          <div className="comparison-card">
                            <h5 className="comparison-criteria">Начало игры</h5>
                            <div className="comparison-items">
                              <div className="comparison-item old">
                                <span className="comparison-label">С ведущим:</span>
                                <span className="comparison-value">Нужно записаться, ждать группу или время мастера</span>
                              </div>
                              <div className="comparison-item new">
                                <span className="comparison-label">С AI:</span>
                                <span className="comparison-value">Мгновенный старт 24/7, в любое время</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-12">
                          <div className="comparison-card">
                            <h5 className="comparison-criteria">Ведущий</h5>
                            <div className="comparison-items">
                              <div className="comparison-item old">
                                <span className="comparison-label">С ведущим:</span>
                                <span className="comparison-value">Человек-проводник, качество зависит от его опыта</span>
                              </div>
                              <div className="comparison-item new">
                                <span className="comparison-label">С AI:</span>
                                <span className="comparison-value">Персональный AI-наставник, внимателен к твоему запросу</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-12">
                          <div className="comparison-card">
                            <h5 className="comparison-criteria">Формат</h5>
                            <div className="comparison-items">
                              <div className="comparison-item old">
                                <span className="comparison-label">С ведущим:</span>
                                <span className="comparison-value">Онлайн по видеосвязи или оффлайн, 3–10 часов</span>
                              </div>
                              <div className="comparison-item new">
                                <span className="comparison-label">С AI:</span>
                                <span className="comparison-value">Онлайн в чате, гибко по времени (от 20 мин до нескольких часов)</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-12">
                          <div className="comparison-card">
                            <h5 className="comparison-criteria">Стоимость</h5>
                            <div className="comparison-items">
                              <div className="comparison-item old">
                                <span className="comparison-label">С ведущим:</span>
                                <span className="comparison-value">От $40 за групповую и от $80 за индивидуальную сессию</span>
                              </div>
                              <div className="comparison-item new">
                                <span className="comparison-label">С AI:</span>
                                <span className="comparison-value">Доступный формат — AI работает без ограничений</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-12">
                          <div className="comparison-card">
                            <h5 className="comparison-criteria">Комфорт</h5>
                            <div className="comparison-items">
                              <div className="comparison-item old">
                                <span className="comparison-label">С ведущим:</span>
                                <span className="comparison-value">Часто в группе, нужно делиться личным</span>
                              </div>
                              <div className="comparison-item new">
                                <span className="comparison-label">С AI:</span>
                                <span className="comparison-value">Полная анонимность и индивидуальная работа</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-12">
                          <div className="comparison-card">
                            <h5 className="comparison-criteria">Результат</h5>
                            <div className="comparison-items">
                              <div className="comparison-item old">
                                <span className="comparison-label">С ведущим:</span>
                                <span className="comparison-value">Зависит от качества ведущего и группы</span>
                              </div>
                              <div className="comparison-item new">
                                <span className="comparison-label">С AI:</span>
                                <span className="comparison-value">Персонализированный отчёт и ясный план после игры</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 text-center">
                      <p className="lead mb-4">
                        👉 В нашей версии ведущий есть — это твой личный AI-наставник. Он не устаёт, не отвлекается и всегда сосредоточен на тебе.
                      </p>
                      <button
                        className={`btn btn-primary btn-lg hero-cta-button ${isCreating ? "magic-animation" : ""}`}
                        onClick={handleCreateGameClick}
                        disabled={isCreating}
                      >
                        {isCreating ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Создаём вашу игру…
                          </>
                        ) : (
                          <>🚀 Войти и пройти игру с AI-наставником</>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Для кого эта игра */}
            <section className="mb-5" id="for-whom">
              <div className="row">
                <div className="col-lg-10 mx-auto">
                  <div className="value-card">
                    <h3 className="value-title mb-4">💡 Для кого эта игра</h3>
                    <h4 className="mb-4">Лила помогает в самых важных сферах жизни:</h4>
                    <div className="row g-4">
                      <div className="col-lg-6 col-md-6">
                        <div className="feature-item">
                          <div className="feature-icon">🔑</div>
                          <div className="feature-content">
                            <h5>Деньги: снять блоки и открыть поток ресурсов</h5>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-6">
                        <div className="feature-item">
                          <div className="feature-icon">💞</div>
                          <div className="feature-content">
                            <h5>Отношения: увидеть, что мешает гармонии</h5>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-6">
                        <div className="feature-item">
                          <div className="feature-icon">🌱</div>
                          <div className="feature-content">
                            <h5>Самореализация: найти своё предназначение и дело по душе</h5>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-6">
                        <div className="feature-item">
                          <div className="feature-icon">😌</div>
                          <div className="feature-content">
                            <h5>Баланс: перестать бороться с собой и обрести ясность</h5>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-center mt-4">
                      <button
                        className={`btn btn-primary btn-lg hero-cta-button ${isCreating ? "magic-animation" : ""}`}
                        onClick={handleCreateGameClick}
                        disabled={isCreating}
                      >
                        {isCreating ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Создаём вашу игру…
                          </>
                        ) : (
                          <>🌟 Выберите свой запрос и начните игру</>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Call to Action (центральный) */}
            <div className="cta-section text-center mb-5">
              <div className="cta-card d-flex flex-column align-items-center gap-2">
                <h3 className="cta-title mb-3">Готовы начать свой путь трансформации?</h3>
                <p className="cta-description mb-4">
                  Войдите через <strong>Google</strong>, <strong>Email</strong> или <strong>Telegram</strong>, чтобы сохранить прогресс и возвращаться в игру в любое время.
                </p>
                <button
                  className={`btn btn-primary btn-lg hero-cta-button ${isCreating ? "magic-animation" : ""}`}
                  onClick={handleCreateGameClick}
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Создаём вашу игру…
                    </>
                  ) : (
                    <>🚀 Войти и начать игру</>
                  )}
                </button>
              </div>
            </div>

            {/* Истории игроков */}
            <section className="trust-section mt-5 pt-5 border-top" id="voc">
              <div className="row">
                <div className="col-lg-10 mx-auto text-start">
                  <h4 className="trust-title mb-4">🌟 Истории игроков</h4>
                  <h5 className="mb-4">Что меняется после игры?</h5>
                  <div className="row g-4">
                    <div className="col-md-6">
                      <blockquote className="blockquote">
                        <p className="mb-2">«После игры я переехала в Таиланд»</p>
                        <footer className="blockquote-footer">Лера, 32 года</footer>
                      </blockquote>
                    </div>
                    <div className="col-md-6">
                      <blockquote className="blockquote">
                        <p className="mb-2">«Нашла мужчину и получила предложение о замужестве»</p>
                        <footer className="blockquote-footer">Злата, 30 лет</footer>
                      </blockquote>
                    </div>
                    <div className="col-md-6">
                      <blockquote className="blockquote">
                        <p className="mb-2">«Купила квартиру, встретила достойного партнёра, живём счастливо»</p>
                        <footer className="blockquote-footer">Алёна, 45 лет</footer>
                      </blockquote>
                    </div>
                    <div className="col-md-6">
                      <blockquote className="blockquote">
                        <p className="mb-2">«Стала принимать поддержку мужа — доход в семье вырос в 2 раза»</p>
                        <footer className="blockquote-footer">Лилия, 28 лет</footer>
                      </blockquote>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <button
                      className={`btn btn-primary btn-lg hero-cta-button ${isCreating ? "magic-animation" : ""}`}
                      onClick={handleCreateGameClick}
                      disabled={isCreating}
                    >
                      {isCreating ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Создаём вашу игру…
                        </>
                      ) : (
                        <>🔮 Войти и получить свой инсайт</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* Конфиденциальность */}
            <section className="trust-section mt-5 pt-5 border-top" id="privacy">
              <div className="row">
                <div className="col-lg-8 mx-auto text-center">
                  <h4 className="trust-title mb-4">🔐 Конфиденциальность</h4>
                  <h5 className="mb-4">Безопасное пространство для твоих инсайтов:</h5>
                  <div className="row g-4 text-start">
                    <div className="col-md-4">
                      <div className="feature-item">
                        <div className="feature-icon">👤</div>
                        <div className="feature-content">
                          <h6>Полная анонимность</h6>
                          <p>Можно играть под любым именем</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="feature-item">
                        <div className="feature-icon">🤖</div>
                        <div className="feature-content">
                          <h6>Только ты и AI-наставник</h6>
                          <p>Никакой группы и посторонних</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="feature-item">
                        <div className="feature-icon">📱</div>
                        <div className="feature-content">
                          <h6>Доступ с любого устройства</h6>
                          <p>Прогресс сохраняется</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <button
                      className={`btn btn-primary btn-lg hero-cta-button ${isCreating ? "magic-animation" : ""}`}
                      onClick={handleCreateGameClick}
                      disabled={isCreating}
                    >
                      {isCreating ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Создаём вашу игру…
                        </>
                      ) : (
                        <>🛡 Начать игру в личном пространстве</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section className="trust-section mt-5 pt-5 border-top" id="faq">
              <div className="row">
                <div className="col-lg-8 mx-auto">
                  <h4 className="trust-title mb-4">❓ FAQ</h4>
                  <div className="accordion" id="faqAccordion">
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="headingOne">
                        <button 
                          className="accordion-button" 
                          type="button" 
                          data-bs-toggle="collapse" 
                          data-bs-target="#collapseOne" 
                          aria-expanded="true" 
                          aria-controls="collapseOne"
                        >
                          Чем отличается от классической Лилы?
                        </button>
                      </h2>
                      <div 
                        id="collapseOne" 
                        className="accordion-collapse collapse show" 
                        aria-labelledby="headingOne" 
                        data-bs-parent="#faqAccordion"
                      >
                        <div className="accordion-body">
                          → Ведущий — это AI. Игра доступна в любое время, без групп, без ожиданий.
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="headingTwo">
                        <button 
                          className="accordion-button collapsed" 
                          type="button" 
                          data-bs-toggle="collapse" 
                          data-bs-target="#collapseTwo" 
                          aria-expanded="false" 
                          aria-controls="collapseTwo"
                        >
                          Это действительно работает без живого человека?
                        </button>
                      </h2>
                      <div 
                        id="collapseTwo" 
                        className="accordion-collapse collapse" 
                        aria-labelledby="headingTwo" 
                        data-bs-parent="#faqAccordion"
                      >
                        <div className="accordion-body">
                          Да. AI-наставник:
                          <ul className="mt-2">
                            <li>Помнит весь ваш диалог и отслеживает трансформацию запроса шаг за шагом</li>
                            <li>Не забывает детали, в отличие от человека</li>
                            <li>Не осуждает и не навязывает своё мнение</li>
                            <li>Независим от настроения, усталости или личного опыта</li>
                            <li>Доступен 24/7, где бы вы ни находились</li>
                            <li>Создаёт абсолютно конфиденциальное пространство: только вы и ваш путь</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="headingThree">
                        <button 
                          className="accordion-button collapsed" 
                          type="button" 
                          data-bs-toggle="collapse" 
                          data-bs-target="#collapseThree" 
                          aria-expanded="false" 
                          aria-controls="collapseThree"
                        >
                          Сколько длится игра?
                        </button>
                      </h2>
                      <div 
                        id="collapseThree" 
                        className="accordion-collapse collapse" 
                        aria-labelledby="headingThree" 
                        data-bs-parent="#faqAccordion"
                      >
                        <div className="accordion-body">
                          → От 20 минут до нескольких часов — вы выбираете темп сами.
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="headingFour">
                        <button 
                          className="accordion-button collapsed" 
                          type="button" 
                          data-bs-toggle="collapse" 
                          data-bs-target="#collapseFour" 
                          aria-expanded="false" 
                          aria-controls="collapseFour"
                        >
                          Какие запросы можно прорабатывать?
                        </button>
                      </h2>
                      <div 
                        id="collapseFour" 
                        className="accordion-collapse collapse" 
                        aria-labelledby="headingFour" 
                        data-bs-parent="#faqAccordion"
                      >
                        <div className="accordion-body">
                          → Любые. Чаще всего игра помогает в сферах денег, отношений, самореализации и поиска внутреннего баланса. Но вы можете прийти с любым вопросом — игра адаптируется под ваш запрос.
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <button
                      className={`btn btn-primary btn-lg hero-cta-button ${isCreating ? "magic-animation" : ""}`}
                      onClick={handleCreateGameClick}
                      disabled={isCreating}
                    >
                      {isCreating ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Создаём вашу игру…
                        </>
                      ) : (
                        <>🚀 Войти и пройти свою первую игру</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* Блок доверия */}
            <section className="trust-section mt-5 pt-5 border-top" id="trust">
              <div className="row">
                <div className="col-lg-8 mx-auto text-center">
                  <h4 className="trust-title mb-4">🕉️ Блок доверия</h4>
                  <div className="mb-4">
                    <p className="text-muted small mb-2">
                      <strong>Основано на книге Сергея Будха «Квантовый перекрёсток»</strong>
                    </p>
                    <p className="text-muted small mb-0">
                      <strong>Поддержка современных AI-технологий (Claude 4.5)</strong>
                    </p>
                  </div>
                  
                  <h5 className="mb-4">Кто стоит за этой игрой?</h5>
                  <div className="text-start">
                    <p className="mb-3">
                      Эту версию «Лилы» я создал изначально для себя.
                      В какой-то момент я искал ответы — как двигаться дальше, где мои ресурсы и что мешает жить счастливее. Игра стала для меня ключом к ясности и внутренней свободе.
                    </p>
                    <p className="mb-3">
                      Потом я дал её друзьям. И у каждого произошёл свой поворот:
                    </p>
                    <ul className="text-start mb-4">
                      <li>кто-то открыл успешный бизнес,</li>
                      <li>кто-то нашёл настоящие отношения,</li>
                      <li>кто-то избавился от барьеров и страхов, которые держали годами.</li>
                    </ul>
                    <p className="mb-4">
                      Так «Лила» в новом формате — <strong>«Квантовый Перекрёсток»</strong> — стала доступна всем.
                    </p>
                    
                    <div className="mb-4">
                      <h6>Контакты:</h6>
                      <p className="mb-2">
                        Я всегда открыт для идей и предложений.
                      </p>
                      <p className="mb-3">
                        <a href="https://quantumgame.love/contact.html" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary btn-sm">
                          Связаться со мной →
                        </a>
                      </p>
                    </div>
                    
                    <blockquote className="blockquote text-center">
                      <p className="mb-0">
                        <em>"Первый шаг к своей истинной природе начинается с осознанного выбора. Лила — это проводник на этом пути."</em>
                      </p>
                    </blockquote>
                  </div>
                  
                  <div className="mt-4">
                    <h6>👉 Попробуй игру. Сегодня ты получишь инсайт, который изменит твою жизнь.</h6>
                    <button
                      className={`btn btn-primary btn-lg hero-cta-button ${isCreating ? "magic-animation" : ""}`}
                      onClick={handleCreateGameClick}
                      disabled={isCreating}
                    >
                      {isCreating ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Создаём вашу игру…
                        </>
                      ) : (
                        <>✨ Начать игру прямо сейчас</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </section>

          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-5 pt-4 border-top">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <p className="text-muted mb-2">
                <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy</a> · 
                <a href="/cookies.html" target="_blank" rel="noopener noreferrer">Cookies</a> · 
                <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms</a> · 
                <a href="/contact.html" target="_blank" rel="noopener noreferrer">Contact</a> · 
                <a href="/impressum.html" target="_blank" rel="noopener noreferrer">Impressum</a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default LillaLandingPage;